import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import socket from "../lib/socket";

function useTransparentBody() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlBg: html.style.background,
      htmlBgImg: html.style.backgroundImage,
      bodyBg: body.style.background,
      bodyBgImg: body.style.backgroundImage,
      bodyMargin: body.style.margin,
      bodyOverflow: body.style.overflow,
    };

    html.style.background = "transparent";
    html.style.backgroundImage = "none";
    body.style.background = "transparent";
    body.style.backgroundImage = "none";
    body.style.margin = "0";
    body.style.overflow = "hidden";

    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundImage = prev.htmlBgImg;
      body.style.background = prev.bodyBg;
      body.style.backgroundImage = prev.bodyBgImg;
      body.style.margin = prev.bodyMargin;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);
}

function normalizeChannelSlug(channelId) {
  const raw = String(channelId || "").trim();
  if (!raw) return "";
  if (raw.includes(":")) {
    const parts = raw.split(":").map((x) => x.trim()).filter(Boolean);
    return parts[parts.length - 1] || "";
  }
  return raw;
}

function roomVariantsForSlug(slug) {
  const key = String(slug || "").trim();
  if (!key) return [];
  return [`live:${key}`, `vod:${key}`];
}

function roomMatches(payloadRoom, rooms) {
  const value = String(payloadRoom || "").trim();
  if (!value) return true;
  return rooms.includes(value);
}

function choiceLabel(choice, emoji) {
  const text = String(choice || "").toUpperCase();
  return `${emoji || "🎮"} ${text}`.trim();
}

function RpsOverlayCard({ result }) {
  if (!result) return null;

  const tie = result.outcome === "tie";
  const winner = result.winner;
  const loser = result.loser;
  const a = result.playerA || {};
  const b = result.playerB || {};

  const title = tie
    ? "RPS STALEMATE"
    : `${String(winner?.name || "Winner").toUpperCase()} WINS`;
  const sub = tie
    ? `${a.name || "Player 1"} and ${b.name || "Player 2"} both picked ${String(result.choiceA || "").toUpperCase()}`
    : `${winner?.name || "Winner"} defeated ${loser?.name || "Opponent"}`;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: tie
          ? "radial-gradient(circle at center, rgba(255,255,255,0.14), transparent 58%)"
          : "radial-gradient(circle at center, rgba(255,0,55,0.18), rgba(0,0,0,0.02) 54%, transparent 74%)",
      }}
    >
      <style>{`
        @keyframes gaRpsPop {
          0% { opacity: 0; transform: translateY(4vh) scale(.78) rotateX(18deg); filter: blur(10px); }
          12% { opacity: 1; transform: translateY(-2vh) scale(1.05) rotateX(0deg); filter: blur(0); }
          22% { transform: translateY(-2vh) scale(1); }
          82% { opacity: 1; transform: translateY(-2vh) scale(1); filter: blur(0); }
          100% { opacity: 0; transform: translateY(-4vh) scale(.94); filter: blur(5px); }
        }
        @keyframes gaRpsSpin {
          from { transform: rotate(0deg) scale(1); opacity: .34; }
          to { transform: rotate(360deg) scale(1.08); opacity: .12; }
        }
        @keyframes gaRpsSlash {
          0% { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
          20% { opacity: .75; }
          100% { transform: translateX(130%) skewX(-18deg); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          width: "min(880px, 90vw)",
          minHeight: 280,
          borderRadius: 38,
          border: "1px solid rgba(255,255,255,0.22)",
          background: tie
            ? "linear-gradient(135deg, rgba(35,35,45,0.90), rgba(0,0,0,0.82), rgba(255,255,255,0.10))"
            : "linear-gradient(135deg, rgba(90,0,18,0.92), rgba(0,0,0,0.84), rgba(0,170,255,0.16))",
          boxShadow: tie
            ? "0 0 55px rgba(255,255,255,0.22), inset 0 0 70px rgba(255,255,255,0.06)"
            : "0 0 60px rgba(255,0,70,0.42), inset 0 0 90px rgba(255,255,255,0.07)",
          color: "#fff",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
          padding: "30px 34px",
          overflow: "hidden",
          position: "relative",
          animation: "gaRpsPop 7.2s ease both",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 620,
            height: 620,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.12)",
            boxShadow: "inset 0 0 60px rgba(255,255,255,0.10)",
            animation: "gaRpsSpin 7.2s linear both",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: "42% -20% auto -20%",
            height: 48,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
            filter: "blur(1px)",
            animation: "gaRpsSlash 1.35s ease-out both",
          }}
        />
        <div
          style={{
            position: "relative",
            fontSize: "clamp(15px, 1.8vw, 24px)",
            fontWeight: 950,
            letterSpacing: 5,
            textTransform: "uppercase",
            color: tie ? "#f1f5f9" : "#ffd166",
          }}
        >
          Rock Paper Scissors
        </div>
        <div
          style={{
            position: "relative",
            fontSize: "clamp(38px, 6vw, 82px)",
            lineHeight: 0.95,
            fontWeight: 1000,
            letterSpacing: 1,
            textTransform: "uppercase",
            textShadow: "0 5px 0 rgba(0,0,0,0.80), 0 0 34px rgba(255,255,255,0.20)",
          }}
        >
          {title}
        </div>
        <div
          style={{
            position: "relative",
            fontSize: "clamp(16px, 2vw, 26px)",
            fontWeight: 900,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.90)",
          }}
        >
          {sub}
        </div>
        <div
          style={{
            position: "relative",
            display: "flex",
            gap: 18,
            flexWrap: "wrap",
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          <div style={pillStyle}>
            {a.name || "Player 1"}: {choiceLabel(result.choiceA, result.choiceAEmoji)} • {result.aRecord || "0-0"}
          </div>
          <div style={pillStyle}>
            {b.name || "Player 2"}: {choiceLabel(result.choiceB, result.choiceBEmoji)} • {result.bRecord || "0-0"}
          </div>
        </div>
      </div>
    </div>
  );
}

const pillStyle = {
  padding: "12px 16px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.18)",
  background: "rgba(0,0,0,0.42)",
  fontWeight: 900,
  letterSpacing: 1,
  textTransform: "uppercase",
  boxShadow: "0 12px 30px rgba(0,0,0,0.28)",
};

export default function OverlayRps() {
  const { channelId } = useParams();
  const location = useLocation();
  const slug = normalizeChannelSlug(channelId);
  const rooms = useMemo(() => roomVariantsForSlug(slug), [slug]);
  const room = rooms[0] || "";
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);
  const debug = useMemo(() => new URLSearchParams(location.search).get("debug") === "1", [location.search]);

  useTransparentBody();

  useEffect(() => {
    if (!socket || !room) return undefined;

    const joinRoom = () => {
      for (const targetRoom of rooms) {
        socket.emit("join", { room: targetRoom, role: "obs" });
        socket.emit("chat:join", { room: targetRoom, role: "obs" });
      }
    };

    const leaveRoom = () => {
      for (const targetRoom of rooms) {
        socket.emit("leave", { room: targetRoom, role: "obs" });
        socket.emit("chat:leave", { room: targetRoom, role: "obs" });
      }
    };

    const onConnect = () => joinRoom();

    const onResult = (payload = {}) => {
      const payloadRoom = String(payload?.room || "").trim();
      if (!roomMatches(payloadRoom, rooms)) return;
      const next = { ...payload, id: `${payload?.id || "rps"}-${payload?.ts || Date.now()}` };
      setResult(next);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setResult((current) => (current?.id === next.id ? null : current));
        timerRef.current = null;
      }, 7200);
    };

    socket.on("connect", onConnect);
    socket.on("rps:result", onResult);

    if (!socket.connected) socket.connect();
    else joinRoom();

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      leaveRoom();
      socket.off("connect", onConnect);
      socket.off("rps:result", onResult);
    };
  }, [room, rooms]);

  useEffect(() => {
    if (!debug) return undefined;
    const demo = window.setTimeout(() => {
      setResult({
        id: `debug-${Date.now()}`,
        room,
        outcome: "a",
        winner: { name: "Tyson" },
        loser: { name: "Slim" },
        playerA: { name: "Tyson" },
        playerB: { name: "Slim" },
        choiceA: "rock",
        choiceB: "scissors",
        choiceAEmoji: "🪨",
        choiceBEmoji: "✂️",
        aRecord: "1-0",
        bRecord: "0-1",
        ts: Date.now(),
      });
    }, 600);
    return () => window.clearTimeout(demo);
  }, [debug, room]);

  return <RpsOverlayCard result={result} />;
}
